import { TenantRole, TenantUserSettings, User } from '@/api/generatedApi';
import { api } from '@/services/api';
import { getTenantShortNameFromUrl } from '@/services/tenants/utils';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export const GET_CURRENT_USER_QUERY_KEY = 'users.getCurrentUser';

export const useGetCurrentUser = ({
  skipRedirect = false,
  retry = true,
}: {
  skipRedirect?: boolean;
  retry?: boolean;
} = {}) => {
  const location = useLocation();
  const currentTenantShortName = getTenantShortNameFromUrl(location.pathname);

  const { data, isLoading, error } = useQuery({
    queryKey: [GET_CURRENT_USER_QUERY_KEY],
    queryFn: api.users.getCurrentUser,
    select: data => data.data,
    meta: {
      skipRedirect,
    },
    retry,
  });

  const currentTenantSettings: TenantUserSettings | undefined = data?.user?.settings?.find(
    tenantSettings => tenantSettings.tenantShortName === currentTenantShortName
  );

  const requireRoles = (roles: TenantRole[]): boolean => {
    if (!currentTenantSettings) {
      return false;
    }
    const { tenantRole, userEnabled } = currentTenantSettings;
    return Boolean(tenantRole && roles.includes(tenantRole) && userEnabled);
  };

  const onlyRole = (role: TenantRole) => {
    if (!currentTenantSettings) {
      return false;
    }
    return currentTenantSettings.tenantRole === role && Boolean(currentTenantSettings.userEnabled);
  };

  const canOnlySearch: boolean = onlyRole(TenantRole.Search);
  const canManageUsers: boolean = requireRoles([TenantRole.Admin]);
  const canUploadAssets: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canDeleteAssets: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canReprocessAssets: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canModifyAssetMetadata: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canAddMetadataFields: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canCreateFolders: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canDeleteFolders: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canManageMetadataFields: boolean = requireRoles([TenantRole.Admin]);
  const canManageReportGroups: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canManageTaxonomy: boolean = requireRoles([TenantRole.Admin]);
  const canSetAssetLabels: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const isAdmin: boolean = requireRoles([TenantRole.Admin]);
  const isAuthor: boolean = requireRoles([TenantRole.Author]);
  const isViewer: boolean = requireRoles([TenantRole.Viewer]);

  const tenantRoleLabel = useMemo((): string => {
    if (isAdmin) return 'Admin';
    if (isAuthor) return 'Author';
    if (isViewer) return 'Viewer';
    return 'User';
  }, [isAdmin, isAuthor, isViewer]);

  const isUserCurrentUser = (user: User) => {
    return user.email === data?.user.displayName;
  };

  const selfAwareEmail = (email: string) => {
    return `${email === data?.user.displayName ? 'Me' : email}`;
  };

  const currentUserIsAtleastTenantViewer = (tenantId?: string): boolean => {
    if (!tenantId) {
      return false;
    }

    const userTenantSettings = data?.user?.settings?.find(tenant => tenant.tenantId === tenantId && tenant.userEnabled);

    if (!userTenantSettings?.tenantRole) {
      return false;
    }

    return [TenantRole.Viewer, TenantRole.Author, TenantRole.Admin].includes(userTenantSettings.tenantRole);
  };

  const enabledTenants = (data?.user?.settings || []).filter(({ userEnabled }) => userEnabled);

  return {
    data,
    isLoading,
    error,
    isUserCurrentUser,
    tenants: enabledTenants,
    tenantRoleLabel,
    currentTenant: currentTenantSettings,
    currentUserIsAtleastTenantViewer,
    selfAwareEmail,
    permissions: {
      canOnlySearch,
      canManageUsers,
      canUploadAssets,
      canDeleteAssets,
      canReprocessAssets,
      canModifyAssetMetadata,
      canAddMetadataFields,
      canCreateFolders,
      canDeleteFolders,
      canManageMetadataFields,
      canManageReportGroups,
      canManageTaxonomy,
      canSetAssetLabels,
    },
  };
};
